import { env } from './env';

// Per-person navigation preferences (pins, collapsed sections, last-used module) in localStorage,
// scoped to the signed-in account AND the environment, so one person's choices never show for
// another on a shared machine and QA never bleeds into production. Preferences are never grants:
// every value read here is filtered against the resolved navigation before it is shown.

export type PreferenceSubject = string | number | null | undefined;

const keyFor = (name: string, subject: PreferenceSubject): string | null =>
  subject == null || subject === '' ? null : `agentstudio.${name}.${env.appEnv}.${subject}`;

export function readPreference<T>(name: string, subject: PreferenceSubject): T | null {
  const key = keyFor(name, subject);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw == null ? null : (JSON.parse(raw) as T);
  } catch {
    return null; // unavailable storage or bad JSON — start fresh
  }
}

export function writePreference(name: string, subject: PreferenceSubject, value: unknown): void {
  const key = keyFor(name, subject);
  if (!key) return;
  try {
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage full or blocked — non-fatal
  }
}

/** One-time move of an UNSCOPED legacy value into this person's scoped key: the first account to sign in after the
 *  upgrade keeps what the browser had; the legacy key is then removed so nobody else inherits it. */
export function adoptLegacyPreference<T>(legacyKey: string, name: string, subject: PreferenceSubject): T | null {
  if (keyFor(name, subject) == null) return null;
  try {
    const raw = window.localStorage.getItem(legacyKey);
    if (raw == null) return null;
    window.localStorage.removeItem(legacyKey);
    const value = JSON.parse(raw) as T;
    if (readPreference(name, subject) == null) writePreference(name, subject, value);
    return value;
  } catch {
    return null;
  }
}
